import { observer } from 'mobx-react-lite';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Divider, Link, Typography } from '@mui/material';
import type { AccountSummary as AccountSummaryType } from '../types';

const formatRenewal = (date: string | null): string | null =>
  date
    ? new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

/** "Unlimited", or "N tasks remaining" */
const tasksRemainingText = (summary: AccountSummaryType): string => {
  if (summary.is_unlimited) return 'Unlimited';
  const tasks = summary.tasks_remaining;
  return `${tasks} ${tasks === 1 ? 'task' : 'tasks'} remaining`;
};

interface AccountSummaryProps {
  summary: AccountSummaryType;
}

const StatRow = ({ label, value, testId }: { label: string; value: string; testId?: string }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.5 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} data-testid={testId}>
      {value}
    </Typography>
  </Box>
);

/** Plan stats for the client dashboard sidebar. */
export const AccountSummaryCard = observer(({ summary }: AccountSummaryProps) => {
  const renews = formatRenewal(summary.next_billing_cycle_date);

  return (
    <Card>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          Tasks
        </Typography>
        <Typography variant="h4" data-testid="account-summary">
          {summary.is_unlimited ? 'Unlimited' : summary.tasks_remaining}
        </Typography>
        {!summary.is_unlimited && (
          <Typography variant="body2" color="text.secondary">
            {summary.tasks_remaining === 1 ? 'task' : 'tasks'} remaining
          </Typography>
        )}
        {(summary.rollover > 0 || renews) && <Divider sx={{ my: 1.5 }} />}
        {summary.rollover > 0 && <StatRow label="Rolled over" value={String(summary.rollover)} />}
        {renews && <StatRow label="Renews" value={renews} />}
        <Box sx={{ mt: 1.5 }}>
          <Link component={RouterLink} to="/settings/billing" variant="body2">
            Billing
          </Link>
        </Box>
      </CardContent>
    </Card>
  );
});

/** One-line plan status, e.g. under the New Request form. */
export const AccountSummaryLine = observer(({ summary }: AccountSummaryProps) => {
  const renews = formatRenewal(summary.next_billing_cycle_date);
  let text = tasksRemainingText(summary);
  if (!summary.is_unlimited && summary.rollover > 0) {
    text += ` (${summary.rollover} rolled over)`;
  }
  if (renews) {
    text += `, renews ${renews}`;
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap', mt: 0.5 }}>
      <Typography variant="body2" color="text.secondary" data-testid="account-summary">
        {text}
      </Typography>
      <Link component={RouterLink} to="/settings/billing" variant="body2">
        Billing
      </Link>
    </Box>
  );
});

/** Shown on the dashboard and New Request when a limited plan has no tasks left. */
export const OutOfTasksBanner = observer(({ summary }: AccountSummaryProps) => {
  const renews = formatRenewal(summary.next_billing_cycle_date);

  return (
    <Alert
      severity="warning"
      sx={{ mb: 3 }}
      action={
        <Button component={RouterLink} to="/settings/billing" color="inherit" size="small">
          Buy more
        </Button>
      }
    >
      0 tasks remaining
      {renews ? `. More tasks arrive when the plan renews on ${renews}.` : '. Buy more to submit new requests.'}
    </Alert>
  );
});
